import { useTranslation } from "react-i18next";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { formatPremiumWithCurrency } from "../../../utils";
import BackdropModal from "../../common/modal/backdropModal";

const DomesticHelperSummaryModal = ({
  show,
  onClose,
  helperData,
  applicationData,
  quoteData,
}) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  if (!show) return null;

  // Get data from API response - NO CALCULATIONS, use API data directly
  const quotationNumber = quoteData?.data?.quoteNumber || "N/A";
  const apiPremium = parseFloat(quoteData?.data?.premium || 0);
  const apiTax = parseFloat(quoteData?.data?.tax || 0);
  const apiTotalPremium = parseFloat(quoteData?.data?.totalPremium || 0);

  // Use API data directly - NO frontend calculations
  const displayPremium = apiPremium;
  const displayTax = apiTax;
  const displayTotal = apiTotalPremium;

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] text-[24px] not-italic font-semibold leading-6">
            {translate("Summary", "التسعيرة")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full  max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="bg-white rounded-lg max-h-[80vh] min-w-full lg:min-w-[624px] max767:max-w-full max767:min-w-full">
          {/* Summary Details */}
          <div className="rounded-lg summary-content">
            {/* Quotation Number */}
            <div className="flex items-center justify-between bg-[#E8F4D8] px-4 py-2.5 rounded-md mb-2">
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {translate("Quotation Number", "رقم التسعيرة")}
              </span>
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {quotationNumber}
              </span>
            </div>

            {/* Premium - Direct from API */}
            <div className="flex justify-between mb-2 px-0 py-2.5">
              <span className="text-[#1A2024] text-sm not-italic font-medium leading-6">
                {translate("Premium", "المبلغ")}
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-medium leading-6">
                {formatCurrency(displayPremium)}
              </span>
            </div>

            {/* VAT - Direct from API */}
            <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("VAT", "ضريبة القيمة المضافة")} 10%
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrency(displayTax)}
              </span>
            </div>

            {/* Total - Direct from API */}
            <div className="flex justify-between px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {translate("Total", "المجموع")}
              </span>
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {formatCurrency(displayTotal)}
              </span>
            </div>
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default DomesticHelperSummaryModal;
